// SPDX-License-Identifier: AGPL-3.0-or-later

import type {HolidayThemePreset} from '@app/features/holiday/state/HolidayThemePresets';

/**
 * Candlelight amber on warm soot.
 *
 * A `.ts` module rather than a `.css` file on purpose: `scripts/GenerateThemeVariables.ts`
 * recursively scrapes custom properties out of every `.css` under `src/` to build
 * `ThemeVariableManifest.ts`, and a preset stored as CSS would pollute that manifest with its own
 * values.
 *
 * Lightness ramps are lifted from `theme/styles/generated/color-system.css` so the hierarchy that
 * separates channel names from muted timestamps survives the rehue. Saturation routes through
 * `var(--saturation-factor)` so the Accessibility saturation control keeps working.
 *
 * `--accent-danger` / `-success` / `-warning` / `-info` are deliberately left at base values: they
 * signal destructive/confirm, and recolouring them for palette coherence costs more than it gains.
 */
const HALLOWEEN_THEME_PRESET_CSS = `/**
 * @name Halloween
 * @description Candlelight amber on warm soot.
 * @author Fluxer
 * @version 1.0.0
 * @tags holiday, halloween, dark
 */

:root {
	/* Surfaces: warm soot, not flat black — flat #000 erases the plane separation between panes. */
	--background-primary: hsl(28, calc(12% * var(--saturation-factor)), 4.3%);
	--background-secondary: hsl(28, calc(12% * var(--saturation-factor)), 6.7%);
	--background-secondary-lighter: hsl(28, calc(12% * var(--saturation-factor)), 8.6%);
	--background-secondary-alt: hsl(28, calc(12% * var(--saturation-factor)), 10.6%);
	--background-tertiary: hsl(28, calc(12% * var(--saturation-factor)), 7.4%);
	--background-channel-header: hsl(28, calc(12% * var(--saturation-factor)), 5.5%);
	--background-header-primary: hsl(28, calc(12% * var(--saturation-factor)), 5.5%);
	--background-header-primary-hover: hsl(28, calc(12% * var(--saturation-factor)), 9.5%);
	--background-header-secondary: hsl(28, calc(12% * var(--saturation-factor)), 6.6%);
	--guild-list-foreground: hsl(28, calc(12% * var(--saturation-factor)), 9%);
	--background-textarea: hsl(30, calc(14% * var(--saturation-factor)), 8.2%);
	--composer-surface-color: hsl(30, calc(14% * var(--saturation-factor)), 8.2%);
	--form-surface-background: hsl(28, calc(12% * var(--saturation-factor)), 5.5%);

	/* Modifiers carry the amber so hover and selection read as candlelight, not a grey wash. */
	--background-modifier-hover: hsla(36, calc(70% * var(--saturation-factor)), 60%, 0.07);
	--background-modifier-selected: hsla(36, calc(75% * var(--saturation-factor)), 62%, 0.13);
	--background-modifier-accent: hsla(36, calc(60% * var(--saturation-factor)), 60%, 0.15);
	--background-modifier-accent-focus: hsla(36, calc(70% * var(--saturation-factor)), 62%, 0.24);

	/* Text: the base system's lightness ramp, rehued to candlelight. */
	--text-primary: hsl(40, calc(100% * var(--saturation-factor)), 72%);
	--text-secondary: hsl(38, calc(92% * var(--saturation-factor)), 66%);
	--text-primary-muted: hsl(36, calc(72% * var(--saturation-factor)), 58%);
	--text-tertiary: hsl(34, calc(58% * var(--saturation-factor)), 52%);
	--text-tertiary-secondary: hsl(32, calc(48% * var(--saturation-factor)), 46%);
	--text-tertiary-muted: hsl(30, calc(40% * var(--saturation-factor)), 41%);

	/* Body copy stays parchment; amber at reading length is fatiguing. */
	--text-chat: hsl(31, calc(58% * var(--saturation-factor)), 78%);
	--text-chat-muted: hsl(31, calc(42% * var(--saturation-factor)), 64%);

	--accent-primary: hsl(24, calc(92% * var(--saturation-factor)), 52%);

	--panel-control-bg: color-mix(
		in srgb,
		var(--background-secondary-alt) 90%,
		hsl(28, calc(12% * var(--saturation-factor)), 2%) 10%
	);
	--panel-control-border: hsla(36, calc(55% * var(--saturation-factor)), 55%, 0.4);
	--panel-control-divider: hsla(36, calc(45% * var(--saturation-factor)), 48%, 0.3);
	--panel-control-highlight: hsla(40, calc(80% * var(--saturation-factor)), 70%, 0.05);

	--control-button-normal-text: var(--text-primary-muted);
	--control-button-hover-bg: hsl(28, calc(12% * var(--saturation-factor)), 12%);
	--control-button-hover-text: var(--text-primary);
	--control-button-active-bg: hsl(28, calc(14% * var(--saturation-factor)), 14%);
	--control-button-active-text: var(--text-primary);

	--scrollbar-thumb-bg: hsla(34, calc(40% * var(--saturation-factor)), 45%, 0.5);
	--scrollbar-thumb-bg-hover: hsla(36, calc(55% * var(--saturation-factor)), 55%, 0.7);
	--scrollbar-track-bg: transparent;
}
`;

export const HALLOWEEN_THEME_PRESET: HolidayThemePreset = {
	packId: 'halloween',
	name: 'Halloween',
	css: HALLOWEEN_THEME_PRESET_CSS,
};
